// RSK-A03-002 + RSK-A08-002: tenant routing kanonik kaynağı `src/shared/finmony-ui/utils/tenant.ts`.
// Bu Vite + Vue 3 projesi `@finmony/ui` import'unu doğrudan tüketmiyor; aşağıdaki listeler
// kanonik kaynakla 1:1 aynı tutulmalıdır. Listeler değişirse aynı PR'da
// `src/shared/finmony-ui/utils/tenant.ts` ve backend `TenantHostResolution.cs` da güncellenmelidir.
import { buildSameOriginApiBase, isCustomDomainHost, parseLoginBranding, sanitizeLogoUrl, sanitizeTenantSlug } from './customDomainHost.mjs'
import { CUSTOM_DOMAIN_LOGIN_PATH } from './customDomainLogin.mjs'

const DEFAULT_ROOT_DOMAIN = 'finmony.com'

/** Portal giriş ekranıyla aynı görünüm için marka ayarları (parseLoginBranding çıktısı). */
export type CustomDomainLoginBranding = ReturnType<typeof parseLoginBranding>

export interface CustomDomainContext {
  tenantSlug: string | null
  /** Platform firma kimliği (GUID); alan adındaki giriş bu firmaya kilitlenir. */
  companyId: string | null
  companyName: string | null
  logoUrl: string | null
  isGuestPaymentAllowed: boolean
  loginBranding: CustomDomainLoginBranding
}

export interface CustomDomainContextInput {
  tenantSlug?: string | null
  companyId?: string | null
  companyName?: string | null
  logoUrl?: string | null
  isGuestPaymentAllowed?: boolean | null
  loginBranding?: CustomDomainLoginBranding | null
}

// Özel alan adı modunda main.ts bootstrap'ı tenant-branding yanıtından doldurur.
let customDomainContext: CustomDomainContext | null = null

const RESERVED_TENANT_LABELS = new Set([
  'portal',
  'api',
  'portaltest',
  'apitest',
  'portalpreprod',
  'apipreprod',
  'hesap',
  'hesap-api',
  'hesap-store',
  'tahsilat',
  'tahsilat-api',
  'tahsilat-fatura',
  'tahsilat-fatura-api',
  'hesap-test',
  'hesap-api-test',
  'hesap-store-test',
  'hesap-preprod',
  'hesap-api-preprod',
  'hesap-store-preprod',
  'tahsilat-test',
  'tahsilat-api-test',
  'tahsilat-fatura-test',
  'tahsilat-fatura-api-test',
  'tahsilat-preprod',
  'tahsilat-api-preprod',
  'tahsilat-fatura-preprod',
  'tahsilat-fatura-api-preprod'
])
const TENANT_PRODUCT_SUFFIXES = [
  'tahsilat-fatura-api-preprod',
  'tahsilat-fatura-preprod',
  'tahsilat-fatura-api-test',
  'tahsilat-fatura-test',
  'tahsilat-fatura-api',
  'tahsilat-fatura',
  'tahsilat-api-preprod',
  'tahsilat-preprod',
  'tahsilat-api-test',
  'tahsilat-test',
  'tahsilat-api',
  'tahsilat',
  'hesap-api-preprod',
  'hesap-store-preprod',
  'hesap-preprod',
  'hesap-api-test',
  'hesap-store-test',
  'hesap-test',
  'hesap-api',
  'hesap-store',
  'hesap'
]

const isLegacyProductLabel = (value: string) => {
  return (
    value === 'e-kolay' ||
    value === 'e-hesap' ||
    value.startsWith('e-kolay-') ||
    value.startsWith('e-hesap-')
  )
}

const getRootDomain = () => (import.meta.env.VITE_TENANT_ROOT_DOMAIN || DEFAULT_ROOT_DOMAIN).trim().replace(/\.+$/, '')

export const getTenantRootDomain = () => getRootDomain()

const extractTenantCandidate = (value: string) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (!normalized || RESERVED_TENANT_LABELS.has(normalized) || isLegacyProductLabel(normalized)) return null

  for (const suffix of TENANT_PRODUCT_SUFFIXES) {
    const marker = `-${suffix}`
    if (!normalized.endsWith(marker)) continue

    const tenant = normalized.slice(0, -marker.length)
    if (!tenant || RESERVED_TENANT_LABELS.has(tenant) || isLegacyProductLabel(tenant)) return null
    return tenant
  }

  return normalized
}

/** Adres finmony.com altında bir firmaya ait mi (örn. acme-tahsilat.finmony.com)? Genel tahsilat adresinde false. */
export const hasTenantHost = () => Boolean(resolveTenantSlug())

const resolveTenantSlug = () => {
  if (typeof window === 'undefined') return null
  const rootDomain = getRootDomain()
  const host = window.location.hostname.toLowerCase()
  if (!host.endsWith(`.${rootDomain}`)) return null

  const prefix = host.slice(0, -(rootDomain.length + 1))
  if (!prefix) return null

  const candidate = prefix.split('.')[0]
  return extractTenantCandidate(candidate)
}

export const applyTenantTemplate = (value?: string | null) => {
  if (!value) return ''
  if (!value.includes('{tenant}')) return value

  const tenant = resolveTenantSlug()
  if (tenant) {
    return value.replaceAll('{tenant}', tenant)
  }

  return value
    .replaceAll('{tenant}-', '')
    .replaceAll('-{tenant}', '')
    .replaceAll('{tenant}.', '')
    .replaceAll('.{tenant}', '')
    .replaceAll('{tenant}', '')
}

const resolvePlatformHost = (baseUrl: string, tenant: string) => {
  try {
    const url = new URL(baseUrl)
    const rootDomain = getRootDomain()
    const host = url.hostname.toLowerCase()

    if (host === `portal.${rootDomain}`) {
      url.hostname = `${tenant}.${rootDomain}`
    } else if (host === `portaltest.${rootDomain}`) {
      url.hostname = `${tenant}.portaltest.${rootDomain}`
    } else if (host === `portalpreprod.${rootDomain}`) {
      url.hostname = `portalpreprod.${rootDomain}`
    } else if (host.endsWith(`.${rootDomain}`)) {
      url.hostname = `${tenant}.${host}`
    }

    return url.toString()
  } catch {
    return baseUrl
  }
}

/**
 * Sayfa müşterinin kendi alan adından (finmony.com / localhost / IP dışı) mı sunuluyor?
 * Bu modda API aynı origin `/api` altındadır ve `{tenant}` şablonu kullanılmaz.
 */
export const isCustomDomain = () => {
  if (typeof window === 'undefined') return false
  return isCustomDomainHost(window.location.hostname, getRootDomain())
}

export const setCustomDomainContext = (context: CustomDomainContextInput | null) => {
  customDomainContext = context
    ? {
        tenantSlug: sanitizeTenantSlug(context.tenantSlug),
        companyId: typeof context.companyId === 'string' && context.companyId.trim() ? context.companyId.trim() : null,
        companyName: typeof context.companyName === 'string' && context.companyName.trim() ? context.companyName.trim() : null,
        logoUrl: sanitizeLogoUrl(context.logoUrl),
        isGuestPaymentAllowed: context.isGuestPaymentAllowed !== false,
        loginBranding: context.loginBranding ?? parseLoginBranding(null),
      }
    : null
}

/** Platform API taban adresi (örn. https://api.finmony.com/api); tanımlı değilse boş. */
export const getPlatformApiUrl = () => (import.meta.env.VITE_PLATFORM_API_URL ?? '').trim()

// Portaldan ürün geçişiyle (/auth/callback) müşterinin alan adına gelen oturum, döneceği portal
// origin'iyle işaretlenir. Alan adından doğrudan (/giris) açılan oturumda işaret yoktur.
const PORTAL_SESSION_STORAGE_KEY = 'epaymentPortalOrigin'
const DEFAULT_PLATFORM_URL = 'https://portal.finmony.com'

const getBrowserStorage = (): Storage | null => {
  try {
    if (typeof window === 'undefined') return null
    return window.localStorage ?? null
  } catch {
    return null
  }
}

const toHttpOrigin = (value?: string | null) => {
  if (typeof value !== 'string' || !value.trim()) return null
  try {
    const url = new URL(value.trim())
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.origin : null
  } catch {
    return null
  }
}

/**
 * Bu ortamda dönülebilecek portal origin'leri: ortamın portalı (örn. portal.finmony.com) ve firmanın kendi
 * portal adresi (örn. acme.finmony.com). Yalnız ortam ayarından ve Platform'un verdiği kiracı adından kurulur;
 * müşterinin alan adından türetilmez. İlk eleman varsayılan dönüş adresidir.
 */
const getAllowedPortalOrigins = () => {
  const raw = import.meta.env.VITE_PLATFORM_URL?.trim() || DEFAULT_PLATFORM_URL
  const origins: string[] = []
  const add = (value: string) => {
    const origin = toHttpOrigin(value)
    if (origin && !origins.includes(origin)) origins.push(origin)
  }

  add(applyTenantTemplate(raw))
  const tenant = customDomainContext?.tenantSlug
  if (tenant) {
    add(raw.includes('{tenant}') ? raw.replaceAll('{tenant}', tenant) : resolvePlatformHost(raw, tenant))
  }
  return origins
}

/** Müşterinin alan adında portaldan gelinmişse dönülecek portal origin'i; aksi halde null. */
const getPortalSessionOrigin = () => {
  if (!isCustomDomain()) return null
  let stored: string | null = null
  try {
    stored = getBrowserStorage()?.getItem(PORTAL_SESSION_STORAGE_KEY) ?? null
  } catch {
    return null
  }
  const origin = toHttpOrigin(stored)
  return origin && getAllowedPortalOrigins().includes(origin) ? origin : null
}

/**
 * Portaldan ürün geçişiyle açılan oturumu işaretler. Dönüş adresi, kullanıcının geldiği portal (referrer)
 * izinli origin'lerden biriyse odur; değilse ya da referrer yoksa ortamın portalıdır. finmony.com'da işlem yapmaz.
 */
export const rememberPortalSession = (referrer?: string | null) => {
  if (!isCustomDomain()) return
  const allowed = getAllowedPortalOrigins()
  const fromReferrer = toHttpOrigin(referrer)
  const origin = fromReferrer && allowed.includes(fromReferrer) ? fromReferrer : allowed[0]
  if (!origin) return
  try {
    getBrowserStorage()?.setItem(PORTAL_SESSION_STORAGE_KEY, origin)
  } catch {
    // Depolama kapalıysa oturum doğrudan giriş gibi davranır (portal bağlantıları gizli kalır).
  }
}

/** Portal işaretini siler: alan adından doğrudan giriş yapıldığında ve çıkışta. */
export const clearPortalSession = () => {
  try {
    getBrowserStorage()?.removeItem(PORTAL_SESSION_STORAGE_KEY)
  } catch {
    // yok sayılır
  }
}

/** Müşterinin alan adındaki oturum portaldan ürün geçişiyle mi açıldı? */
export const isPortalSession = () => getPortalSessionOrigin() !== null

/**
 * Müşterinin alan adından doğrudan girildiğinde yalnız tahsilat görünür: portal ana sayfası, ürün geçiş kartları ve
 * diğer ürünlere (e-Kolay Tahsilat Fatura vb.) giden bağlantılar gizlenir. Portaldan ürün geçişiyle gelinmişse
 * bağlantılar açıktır ve kullanıcının geldiği portala gider. finmony.com'da davranış değişmez.
 */
export const portalLinksEnabled = () => !isCustomDomain() || isPortalSession()

/** Alan adındaki tahsilat giriş ekranının mutlak adresi (aynı origin). */
const buildCustomDomainLoginUrl = () => `${window.location.origin}${CUSTOM_DOMAIN_LOGIN_PATH}`

export const getCustomDomainContext = (): CustomDomainContext | null =>
  customDomainContext ? { ...customDomainContext } : null

/**
 * Ham API taban adresini çözer. Özel alan adında her zaman aynı origin `/api` döner
 * (genel tahsilat-api host'una asla düşmez); aksi halde eski `{tenant}` şablon davranışı birebir korunur.
 */
export const resolveApiBaseUrl = (raw?: string | null) => {
  if (isCustomDomain()) {
    return buildSameOriginApiBase(window.location.origin)
  }

  return applyTenantTemplate(raw ?? import.meta.env.VITE_API_BASE ?? '')
}

export const resolveApiBase = () => resolveApiBaseUrl(import.meta.env.VITE_API_BASE ?? '')

export const resolvePlatformUrl = (fallback?: string) => {
  const raw = import.meta.env.VITE_PLATFORM_URL?.trim() || fallback || ''
  if (!raw) return ''

  if (isCustomDomain()) {
    // Portaldan gelen oturum, geldiği portala döner (firma portal adresine çevrilmez; orada oturum olmayabilir).
    const portalOrigin = getPortalSessionOrigin()
    if (portalOrigin) return `${portalOrigin}/`

    const customTenant = customDomainContext?.tenantSlug
    if (!customTenant) {
      return applyTenantTemplate(raw)
    }

    return raw.includes('{tenant}')
      ? raw.replaceAll('{tenant}', customTenant)
      : resolvePlatformHost(raw, customTenant)
  }

  const tenant = resolveTenantSlug()
  if (!tenant) {
    return applyTenantTemplate(raw)
  }

  if (raw.includes('{tenant}')) {
    return applyTenantTemplate(raw)
  }

  return resolvePlatformHost(raw, tenant)
}

export const buildPlatformLoginUrl = (baseUrl?: string | null) => {
  // Müşterinin alan adında giriş portala değil, aynı adresteki tahsilat giriş ekranına yapılır.
  if (isCustomDomain()) return buildCustomDomainLoginUrl()

  const resolved = (baseUrl || resolvePlatformUrl('')).trim()
  if (!resolved) return '/auth/login'

  try {
    const url = new URL(resolved, window.location.origin)
    url.pathname = '/auth/login'
    url.search = ''
    url.hash = ''
    return url.toString()
  } catch {
    const trimmed = resolved.replace(/\/+$/, '')
    return `${trimmed}/auth/login`
  }
}

/**
 * Çıkış adresi. Yalnız çıkış anında, yönlendirmeden hemen önce çağrılır.
 * Müşterinin alan adında: portaldan gelen oturum geldiği portalın çıkışına gider ve portal işareti silinir
 * (sonraki oturum doğrudan girişse bağlantılar görünmesin); doğrudan girişte aynı adresin /giris ekranına döner.
 */
export const buildPlatformLogoutUrl = (baseUrl?: string | null) => {
  if (isCustomDomain()) {
    const portalOrigin = getPortalSessionOrigin()
    clearPortalSession()
    // Doğrudan girişte portal oturumu tutulmaz (girişten hemen sonra kapatılır); çıkış yalnız yerel oturumu siler.
    return portalOrigin ? `${portalOrigin}/auth/login?logout=1` : buildCustomDomainLoginUrl()
  }

  const resolved = (baseUrl || resolvePlatformUrl('')).trim()
  if (!resolved) return '/auth/login?logout=1'

  try {
    const url = new URL(resolved, window.location.origin)
    url.pathname = '/auth/login'
    url.search = 'logout=1'
    url.hash = ''
    return url.toString()
  } catch {
    const trimmed = resolved.replace(/\/+$/, '')
    return `${trimmed}/auth/login?logout=1`
  }
}
